import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@repo/ui'
import { Link, Outlet, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import { Building2, ChevronsUpDown, LogOut, ShieldCheck } from 'lucide-react'
import { apiLogout } from '@/api/user/logout'
import { isSysAdmin } from '@/common/adminRouting'

export const Route = createFileRoute('/_auth/admin')({
  beforeLoad: ({ context }) => {
    if (!isSysAdmin(context.user.userOrgProfile)) {
      throw redirect({ to: '/' })
    }
  },
  component: AdminLayout
})

function toInitials(firstName: string, surname: string, email: string): string {
  const initials = ((firstName.trim()[0] ?? '') + (surname.trim()[0] ?? '')).toUpperCase()
  return initials || (email.trim()[0] ?? '?').toUpperCase()
}

function AdminLayout() {
  const router = useRouter()
  const profile = router.options.context.user.userOrgProfile

  const firstName = profile?.first_name ?? ''
  const surname = profile?.surname ?? ''
  const email = profile?.email ?? ''
  const displayName = [firstName, surname].filter(Boolean).join(' ') || email
  const orgSlug = profile?.org.slug

  async function handleLogout() {
    try {
      await apiLogout()
    } finally {
      // Clear the router's in-memory auth context before navigating. Without this,
      // context.user.isAuthenticated stays true, the /login guard redirects back to
      // '/', and _auth/index bounces a sysadmin back to /admin, where the now
      // session-less API calls surface as an unauthorised error on the page.
      router.update({
        context: {
          ...router.options.context,
          user: { isAuthenticated: false }
        }
      })
      await router.navigate({ to: '/login' })
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 flex h-14 items-center justify-between border-b bg-background px-6">
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck className="size-4" />
          </span>
          <span className="text-sm font-semibold tracking-tight">Snowvault Admin</span>
        </div>

        <div className="flex items-center gap-2">
          {orgSlug ? (
            <Button variant="outline" size="sm" className="h-9 gap-2" asChild>
              <Link to="/$orgSlug" params={{ orgSlug }}>
                <Building2 className="size-4" />
                Go to my org
              </Link>
            </Button>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 gap-2 px-2">
                <Avatar className="size-6">
                  <AvatarFallback className="text-xs">{toInitials(firstName, surname, email)}</AvatarFallback>
                </Avatar>
                <span className="hidden max-w-[16ch] truncate text-sm sm:inline">{displayName}</span>
                <ChevronsUpDown className="size-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col">
                  <span className="truncate text-sm font-medium">{displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{email}</span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void handleLogout()}>
                <LogOut className="size-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-8">
        <Outlet />
      </main>
    </div>
  )
}
